import React, { useState } from 'react';
import * as adminApi from '../../../../api/adminApi';
import modalStyles from './AdminModals.module.css';

interface RevokeAdminModalProps {
  isOpen: boolean;
  onClose: () => void;
  token: string | null;
  admin: adminApi.AdminSummary | null;
  onConfirmRevoke: () => void;
}

export const RevokeAdminModal: React.FC<RevokeAdminModalProps> = ({
  isOpen,
  onClose,
  token,
  admin,
  onConfirmRevoke
}) => {
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen || !admin) return null;

  // An active admin is revoked (deactivated, history kept); a revoked admin is removed for good.
  const removing = !admin.is_active;

  const handleRevoke = async () => {
    if (!confirmChecked || !token) return;

    setRevoking(true);
    setError(null);
    try {
      if (removing) await adminApi.removeAdminAccount(token, String(admin.id));
      else await adminApi.revokeAdminAccess(token, String(admin.id));
      onConfirmRevoke();
      onClose();
      setConfirmChecked(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Request failed');
    } finally {
      setRevoking(false);
    }
  };

  return (
    <div className={modalStyles.overlay} onClick={onClose}>
      <div className={modalStyles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={modalStyles.header}>
          <h2 className={modalStyles.title} style={{ color: 'var(--danger,var(--ds-danger))' }}>{removing ? 'Remove Admin Account' : 'Revoke Admin Access'}</h2>
          <button onClick={onClose} className={modalStyles.closeBtn} aria-label="Close">✕</button>
        </div>

        <div className={modalStyles.body}>
          <div style={{ marginBottom: '1rem' }}>
            <p style={{ margin: 0, fontSize: '0.95rem', color: 'var(--admin-text,var(--ds-text))', lineHeight: 1.5 }}>
              {removing ? 'Remove the account of' : 'Are you sure you want to revoke admin access for'} <strong>{admin.full_name}</strong>? ({admin.email})
            </p>
          </div>

          <div className={modalStyles.dangerNote} style={{ marginBottom: '1rem' }}>
            <p style={{ margin: 0, fontSize: '0.85rem', lineHeight: 1.4 }}> <strong>Warning:</strong> {removing
              ? 'The account is deleted permanently. Documents, reports, transcripts and roles this admin created are handed to you; the audit log keeps its entries.'
              : 'This action will immediately revoke all admin privileges and access to the admin dashboard. They will no longer be able to manage users, documents, or system settings. The account stays listed as inactive and can be removed afterwards.'}</p>
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
            <input type="checkbox" checked={confirmChecked} onChange={(e) => setConfirmChecked(e.target.checked)} style={{ width: '18px', height: '18px', cursor: 'pointer' }} />
            <span style={{ fontSize: '0.95rem', color: 'var(--admin-text,var(--ds-text))' }}>{removing ? 'I confirm that I want to remove this admin account' : 'I confirm that I want to revoke access for this admin'}</span>
          </label>
          {error && <p role="alert" style={{ margin: '0.75rem 0 0', color: 'var(--danger,var(--ds-danger))', fontSize: '0.9rem' }}>{error}</p>}
        </div>

        <div className={modalStyles.footer}>
          <div className={modalStyles.actions}>
            <button onClick={handleRevoke} disabled={!confirmChecked || revoking} className={`${modalStyles.btn} ${modalStyles.danger}`}>{revoking ? (removing ? 'Removing...' : 'Revoking...') : (removing ? 'Remove Account' : 'Revoke Access')}</button>
            <button onClick={onClose} disabled={revoking} className={`${modalStyles.btn} ${modalStyles.neutral}`}>Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
};
